"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCurrencySymbol = exports.getCurrencyCode = exports.formatNumber = exports.formatCurrency = exports.CURRENCY_CONFIGS = void 0;
// Common currency configurations
exports.CURRENCY_CONFIGS = {
    HK: {
        locale: 'zh-HK',
        currency: 'HKD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    },
    TH: {
        locale: 'th-TH',
        currency: 'THB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    },
    TW: {
        locale: 'zh-TW',
        currency: 'TWD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0, // 台幣通常不顯示小數
    },
    US: {
        locale: 'en-US',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    },
    JP: {
        locale: 'ja-JP',
        currency: 'JPY',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0, // 日圓沒有小數
    },
    SG: {
        locale: 'en-SG',
        currency: 'SGD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    },
};
/**
 * Format currency with proper locale and currency symbol
 */
function formatCurrency(amount, countryCode = 'HK', customConfig, options) {
    var _a;
    if (amount === undefined || amount === null) {
        amount = 0;
    }
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) {
        return '0';
    }
    let config = exports.CURRENCY_CONFIGS[countryCode.toUpperCase()];
    if (config === undefined) {
        // Try to find by currency field if not found by country code
        config = Object.values(exports.CURRENCY_CONFIGS).find(c => c.currency === countryCode.toUpperCase());
    }
    if (config === undefined) {
        console.warn(`No currency config found for country: ${countryCode}, using HK as default`);
        return formatCurrency(amount, 'HK', customConfig, options);
    }
    const finalConfig = Object.assign(Object.assign({}, config), customConfig);
    try {
        let formattedCurrency = numericAmount.toLocaleString(finalConfig.locale, {
            style: 'currency',
            currency: finalConfig.currency,
            minimumFractionDigits: finalConfig.minimumFractionDigits,
            maximumFractionDigits: finalConfig.maximumFractionDigits,
        });
        // Remove country prefix if not explicitly requested to include it
        if ((options === null || options === void 0 ? void 0 : options.includeCountryPrefix) !== true) {
            const prefixesToRemove = ['HK', 'TW', 'SG', 'AU', 'CA', 'NZ'];
            for (const prefix of prefixesToRemove) {
                // Handle both positive and negative numbers
                // Patterns: "HK$100", "-HK$100", "(HK$100)"
                if (formattedCurrency.startsWith(prefix)) {
                    formattedCurrency = formattedCurrency.slice(prefix.length);
                    break;
                }
                else if (formattedCurrency.startsWith('-' + prefix)) {
                    formattedCurrency = '-' + formattedCurrency.slice(prefix.length + 1);
                    break;
                }
                else if (formattedCurrency.startsWith('(' + prefix)) {
                    formattedCurrency = '(' + formattedCurrency.slice(prefix.length + 1);
                    break;
                }
            }
        }
        return formattedCurrency;
    }
    catch (error) {
        console.warn(`Error formatting currency for ${countryCode}:`, error);
        // Fallback to simple number formatting
        return numericAmount.toFixed((_a = finalConfig.maximumFractionDigits) !== null && _a !== void 0 ? _a : 2);
    }
}
exports.formatCurrency = formatCurrency;
/**
 * Format number with thousand separators only (no currency symbol)
 */
function formatNumber(amount, countryCode = 'HK', fractionDigits = 2) {
    var _a;
    if (amount === undefined || amount === null) {
        amount = 0;
    }
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) {
        return '0';
    }
    const config = exports.CURRENCY_CONFIGS[countryCode.toUpperCase()];
    const locale = (_a = config === null || config === void 0 ? void 0 : config.locale) !== null && _a !== void 0 ? _a : 'zh-HK';
    try {
        return numericAmount.toLocaleString(locale, {
            minimumFractionDigits: fractionDigits,
        });
    }
    catch (error) {
        console.warn(`Error formatting number for ${countryCode}:`, error);
        return numericAmount.toFixed(fractionDigits);
    }
}
exports.formatNumber = formatNumber;
function getCurrencyCode(countryCode) {
    var _a, _b, _c;
    return (_c = (_b = exports.CURRENCY_CONFIGS[(_a = countryCode === null || countryCode === void 0 ? void 0 : countryCode.toUpperCase()) !== null && _a !== void 0 ? _a : '']) === null || _b === void 0 ? void 0 : _b.currency) !== null && _c !== void 0 ? _c : 'USD';
}
exports.getCurrencyCode = getCurrencyCode;
/**
 * Get currency symbol for a country
 */
function getCurrencySymbol(countryCode, options) {
    var _a, _b;
    const includeCountryPrefix = (_a = options === null || options === void 0 ? void 0 : options.includeCountryPrefix) !== null && _a !== void 0 ? _a : false;
    let config = exports.CURRENCY_CONFIGS[countryCode.toUpperCase()];
    if (config === undefined) {
        // Try to find by currency field
        config = Object.values(exports.CURRENCY_CONFIGS).find(c => c.currency === countryCode.toUpperCase());
    }
    if (config === undefined) {
        return '$'; // Default to US Dollar
    }
    // Create a temporary formatter to extract the currency symbol
    try {
        const formatter = new Intl.NumberFormat(config.locale, {
            style: 'currency',
            currency: config.currency,
        });
        // Format 0 and extract the symbol
        const parts = formatter.formatToParts(0);
        const currencyPart = parts.find(part => part.type === 'currency');
        const symbol = (_b = currencyPart === null || currencyPart === void 0 ? void 0 : currencyPart.value) !== null && _b !== void 0 ? _b : config.currency;
        // If user wants to remove country prefix (like "HK" from "HK$")
        if (!includeCountryPrefix) {
            // Remove common country prefixes from symbols
            const prefixesToRemove = ['HK', 'TW', 'SG', 'AU', 'CA', 'NZ'];
            for (const prefix of prefixesToRemove) {
                if (symbol.startsWith(prefix)) {
                    return symbol.slice(prefix.length);
                }
            }
        }
        return symbol;
    }
    catch (error) {
        return config.currency;
    }
}
exports.getCurrencySymbol = getCurrencySymbol;
//# sourceMappingURL=currency.js.map